import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Button } from './ui/button';
import { Sparkles } from 'lucide-react';

const EVERWASH_URL = 'https://app.everwash.com/#membership-plans/143';

export const FullPageWave = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [50, -150]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.3, 1, 1, 0.3]);

  return (
    <section 
      ref={containerRef}
      className="relative h-[80vh] md:h-screen overflow-hidden bg-gradient-to-b from-[#0F172A] via-[#1E3A8A] to-[#0F172A]"
      data-testid="full-page-wave"
    >
      {/* Animated gradient background */}
      <motion.div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(30, 58, 138, 0.8) 0%, transparent 70%)'
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.5, 0.8, 0.5]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Wave layers */}
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Back wave layer */}
        <motion.div 
          className="absolute w-[200%] h-full"
          style={{ y: y1, opacity }}
        >
          <svg 
            viewBox="0 0 1440 800" 
            className="w-full h-full"
            preserveAspectRatio="none"
          >
            <motion.path
              fill="rgba(59, 130, 246, 0.1)"
              animate={{
                d: [
                  "M0,400 Q360,300 720,400 T1440,400 T2160,400 T2880,400 L2880,800 L0,800 Z",
                  "M0,400 Q360,500 720,400 T1440,400 T2160,400 T2880,400 L2880,800 L0,800 Z",
                  "M0,400 Q360,300 720,400 T1440,400 T2160,400 T2880,400 L2880,800 L0,800 Z"
                ]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>
        </motion.div>

        {/* Middle wave layer */}
        <motion.div 
          className="absolute w-[200%] h-full"
          style={{ y: y2 }}
          animate={{ x: [0, -100, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        >
          <svg 
            viewBox="0 0 1440 800" 
            className="w-full h-full"
            preserveAspectRatio="none"
          >
            <motion.path
              fill="rgba(59, 130, 246, 0.2)"
              animate={{
                d: [
                  "M0,450 Q240,350 480,450 Q720,550 960,450 Q1200,350 1440,450 Q1680,550 1920,450 Q2160,350 2400,450 L2400,800 L0,800 Z",
                  "M0,450 Q240,550 480,450 Q720,350 960,450 Q1200,550 1440,450 Q1680,350 1920,450 Q2160,550 2400,450 L2400,800 L0,800 Z",
                  "M0,450 Q240,350 480,450 Q720,550 960,450 Q1200,350 1440,450 Q1680,550 1920,450 Q2160,350 2400,450 L2400,800 L0,800 Z"
                ]
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>
        </motion.div>

        {/* Front wave layer */}
        <motion.div 
          className="absolute w-[200%] h-full"
          style={{ y: y3 }}
          animate={{ x: [0, 50, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        >
          <svg 
            viewBox="0 0 1440 800" 
            className="w-full h-full"
            preserveAspectRatio="none"
          >
            <motion.path
              fill="rgba(220, 38, 38, 0.15)"
              animate={{
                d: [
                  "M0,500 Q180,420 360,500 Q540,580 720,500 Q900,420 1080,500 Q1260,580 1440,500 Q1620,420 1800,500 L1800,800 L0,800 Z",
                  "M0,500 Q180,580 360,500 Q540,420 720,500 Q900,580 1080,500 Q1260,420 1440,500 Q1620,580 1800,500 L1800,800 L0,800 Z",
                  "M0,500 Q180,420 360,500 Q540,580 720,500 Q900,420 1080,500 Q1260,580 1440,500 Q1620,420 1800,500 L1800,800 L0,800 Z"
                ]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>
        </motion.div>

        {/* Floating water droplets */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white/20"
            style={{
              width: Math.random() * 15 + 5,
              height: Math.random() * 15 + 5,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -300, 0],
              opacity: [0, 0.6, 0],
              scale: [0.5, 1, 0.5]
            }}
            transition={{
              duration: Math.random() * 10 + 8,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut"
            }}
          />
        ))}

        {/* Light rays */}
        <motion.div
          className="absolute w-full h-full"
          style={{
            background: 'conic-gradient(from 180deg at 50% 50%, transparent 0deg, rgba(255,255,255,0.03) 60deg, transparent 120deg, rgba(255,255,255,0.03) 180deg, transparent 240deg, rgba(255,255,255,0.03) 300deg, transparent 360deg)'
          }}
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Content overlay */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <motion.div 
          className="text-center px-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight mb-6"
            animate={{ 
              textShadow: [
                "0 0 20px rgba(255,255,255,0.3)",
                "0 0 40px rgba(255,255,255,0.5)",
                "0 0 20px rgba(255,255,255,0.3)"
              ]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            Make Waves
          </motion.h2>
          <p className="text-lg md:text-xl text-blue-100/80 max-w-2xl mx-auto mb-8">
            Unlimited washes for one low monthly price. Your car deserves the best.
          </p>
          <a href={EVERWASH_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-lg px-10 py-6 rounded-full shadow-lg shadow-red-500/30 hover:shadow-red-500/50 transition-all hover:-translate-y-1">
              <Sparkles className="w-5 h-5 mr-2" />
              Join EverWash
            </Button>
          </a>
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};

export default FullPageWave;
